<template>
  <div id="route" v-if="!loading && !error && route">
    <div id="route-panel-wrapper" :class="{'minimized': minimizePlaces}">
      <RouteLivePanel v-if="route.live" :route="route" :liveState="liveState" />
      <RoutePanel v-else :route="route" />
    </div>
    
    <div id="route-map-wrapper">
      <RouteMap :route="route" :liveState="liveState" v-if="state.showMap" />
    </div>

    <div v-if="!route.live" id="route-settings-wrapper" class="card card-glass no-print" :class="{'active': store.ui.state.route.settingsDrawer}">
      <RouteSettings :route="route" />
    </div>
    
    <Modal v-model="showAssignDriver" closable> <AssignDriver :route="route.data" /></Modal>
  </div> 
  <div v-else-if="error">
    <Modal :modelValue="true">
      <template v-slot:header>
        <h2>Ruten blev ikke fundet</h2>
      </template>
      <div>
        <p>Ruten med id <b>{{routeId}}</b> blev ikke fundet. Måske har ejeren valgt at skjule den.</p>
      </div>
      <template v-slot:footer>
        <AppButton color="primary" @click="$router.push('/')">Tilbage</AppButton>
      </template>
    </Modal>
  </div>
  <div v-else>
    <div id="route-loader">
      <AppLoader />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, onBeforeUnmount, reactive } from 'vue'
import { useRouter } from 'vue-router'

import RoutePanel from '@/views/route/RoutePanel.vue'
import RouteLivePanel from '@/views/route/RouteLivePanel.vue'
import RouteSettings from '@/components/route/RouteSettings.vue'
import RouteMap from '@/components/route/RouteMap.vue'
import AssignDriver from '@/components/modals/AssignDriver.vue'

import { store } from '@/store'

import { setWindowTitle } from '@/api/tools'

export default defineComponent({
  name: 'Route',
  components: { RoutePanel, RouteLivePanel, RouteMap, RouteSettings, AssignDriver },
  setup() {
    const state = reactive({
      showMap: true
    })
    const loading = ref(false)
    const error = ref(false)

    const router = useRouter()
    const routeId = router.currentRoute.value.params.id as string
    
    const route = computed(() => store.route.getRoute(routeId))
    const liveState = computed(() => store.live.getLiveState(routeId))

    watch(() => route.value?.settings.name, name => {      
      setWindowTitle(name || 'Rute ' + routeId)
    }, {deep: true, immediate: true})

    onMounted(async () => {
      if (!route.value) {
        // Route does not exist locally
        loading.value = true
      }
      try {
        await store.route.initialize(routeId)
        loading.value = false
      } catch (err) {
        console.log(err)
        error.value = true
        loading.value = false
      }


      // Hide map on mobile to save map loads
      let width = document.documentElement.clientWidth
      state.showMap = width > 640
      window.addEventListener("resize", () => {
        width = document.documentElement.clientWidth
        state.showMap = width > 640
      })
    })

    onBeforeUnmount(async () => {
      store.route.saveRoute(routeId)
    })

    const minimizePlaces = ref(false)
    const optionsDropdown = ref(false)

    const user = store.user.get(state => state.user)
    const isOwner = computed(() => store.user.isOwner(routeId))

    const showAssignDriver = ref(false)

    return { store, state, route, liveState, loading, error, user, isOwner, routeId, showAssignDriver, optionsDropdown, minimizePlaces }
  },
  /*metaInfo() {
    if (this.route.exists) {
      return { 
        title: this.route.data.settings.name,
        meta: [
          { property: 'og:title', content: this.route.data.settings.name},
          { property: 'og:site_name', content: 'Tripply.dk'},
          { property: 'og:type', content: 'website'},    
          { name: 'robots', content: 'index,follow'} 
        ]
      }
    }
  }*/
})
</script>

<style lang="scss">
#route {
}

#pro {
  border-radius: 1rem;
  padding: 1.5rem;
  margin: 1rem 0;
}

#route-panel-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--panel-width);
  display: flex;
  flex-direction: column;
  padding-top: 0;
  background: var(--color-nav);
  box-shadow: 0px 0px 40px rgba(0,0,0,0.1);
  z-index: 100;

  #route-header {
    display: flex;
    height: 3rem;
    flex: 3rem 0 0;
    align-items: center;
    margin-bottom: 1rem;

    .loader {
      margin-right: 1rem !important;
    }
  }
}
#route-map-wrapper {
  position: absolute;
  top: 0;
  left: var(--panel-width);
  right: 0;
  bottom: 0;
}

#route-settings-wrapper {
  position: absolute;
  top: 1rem;
  left: calc(var(--panel-width) + 1rem);
  width: 24rem;
  padding: var(--side-padding);
  padding-bottom: .5rem;
  transition: transform .3s, opacity .3s;
  z-index: 99;
  opacity: 0;
  transform-origin: top left;
  transform: scale(0.5) translateX(-2rem);
  pointer-events: none;

  &.active {
    opacity: 1;
    pointer-events: all;
    transform: scale(1) translateX(0);
  }
}

#route-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}


@media only screen and (max-width: 640px) {
  #route {
  }
  #route-panel-wrapper {
    width: 100%;
    transition: top .2s;
    &.minimized {
      top: calc(100vh - 13rem);
      padding-top: 1rem;
    }
  }
  #route-map-wrapper {
    left: 0;
    display: none;
  }
  #route-settings-wrapper {
    transform-origin: top right;
    left: var(--margin-left);
    z-index: 101;
    width: 100%;
  }
}

</style>